import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Button } from "react-bootstrap";
import Loader from "../../../Loader/Loader";
import { successToastMessage, errorToastMessage, infoToastMessage } from "../../../../utils/toastMessage";
import { commonService } from "../../../../utils/commonService";
import { apiCall } from "../../../../utils/axiosService";
import { API_URL } from "../../../../utils/apiRoutes";
import { apiResponse } from "../../../../utils/apiResponse";
import { compName } from "../../../../utils/constant";
import { useSelectAPIEndpoint } from "../../../../utils/useSelectAPIEndpoint";
import moment from "moment";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import infoIcon from "../../../../Assets/Images/Action Icons/infoIcon..svg";
import emailIcon from "../../../../Assets/Images/Email.svg";
import downloadIcon from "../../../../Assets/Images/Operation/downloadIcon.svg";
import Comments from "../Comments/Comment";
import WidgetDetailModal from "../../WidgetDetailModal";
import CustomRangePicker from "../../../CommonComponent/CustomRangePIcker";
import DatePicker from "react-datepicker";

import {
  sampleData,
  sampleConfig,
  buildAlignedData,
  ingestData,
  computePromoRadar,
  fmt$,
  pct,
  md,
  dmd,
  dObj,
  vcls,
  LEAK_NAME,
  cleanChannelName,
  mergeChannelsWithConfig,
} from "./PromoRadarComp/promoRadarData";
import PromoConfigModal from "./PromoRadarComp/PromoConfigModal";
import PromoDrawer from "./PromoRadarComp/PromoDrawer";
import PromoBriefModal from "./PromoRadarComp/PromoBriefModal";
import "./PromoRadarComp/PromoRadar.css";

// Lightweight React Virtualization Hook (60FPS windowing without external library dependencies)
function useVirtualList({ totalCount, itemHeight, overscan = 18, containerRef }) {
  const [range, setRange] = useState({ start: 0, end: 35, top: 0, bottom: 0 });
  const rafId = useRef(null);
  const lastStartRef = useRef(0);
  const lastEndRef = useRef(35);

  const updateRange = useCallback(() => {
    if (!containerRef.current) return;
    const st = containerRef.current.scrollTop || 0;
    const vh = Math.max(containerRef.current.clientHeight || 0, window.innerHeight || 800);

    const start = Math.max(0, Math.floor(st / itemHeight) - overscan);
    const end = Math.min(
      Math.max(0, totalCount - 1),
      Math.ceil((st + vh) / itemHeight) + overscan
    );

    if (start !== lastStartRef.current || end !== lastEndRef.current) {
      lastStartRef.current = start;
      lastEndRef.current = end;
      const top = start * itemHeight;
      const bottom = Math.max(0, (totalCount - 1 - end) * itemHeight);
      setRange({ start, end, top, bottom });
    }
  }, [totalCount, itemHeight, overscan, containerRef]);

  useEffect(() => {
    updateRange();
    const handleResize = () => updateRange();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [totalCount, updateRange]);

  const handleScroll = useCallback(() => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(updateRange);
  }, [updateRange]);

  return {
    startIndex: range.start,
    endIndex: range.end,
    topSpacer: range.top,
    bottomSpacer: range.bottom,
    handleScroll,
  };
}

// Skeleton & Placeholder components for virtualized scroll regions
function PromoNamesSpacer({ height }) {
  if (!height || height <= 0) return null;
  const numRows = Math.min(Math.floor(height / 38), 14);
  return (
    <div
      className="cal-names-spacer"
      style={{
        height: `${height}px`,
        position: "relative",
      }}
    >
      <div className="cal-names-grid-pattern" />
      <div className="cal-names-skeleton-rows">
        {Array.from({ length: numRows }).map((_, i) => (
          <div key={i} className="cal-row promo-row skeleton-row">
            <span className="dot skeleton-dot" />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="pname-line">
                <span className="skeleton-badge" />
                <span className="skeleton-line title" />
              </div>
              <div className="pid">
                <span className="skeleton-line sub" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromoMatrixSpacer({ height }) {
  if (!height || height <= 0) return null;
  return (
    <div
      className="cal-matrix-spacer"
      style={{
        height: `${height}px`,
        position: "relative",
      }}
    >
      <div className="cal-matrix-grid-pattern" />
      {height >= 80 && (
        <div className="cal-loading-floater">
          <span className="cal-spinner" />
          <span>Loading promotions...</span>
        </div>
      )}
    </div>
  );
}

export const PromoRadar = ({
  setCompDataFromChild,
  rowIndex,
  colIndex,
  api,
  para,
  setting,
}) => {
  const { propertyCode, snapshotId } = useParams();

  const stlyComsetRatesEndpoint = useSelectAPIEndpoint({
    standard: API_URL.DAILY_SUMMARY.STLY_LY_COMSET_RATES,
    datewise: API_URL.DAILY_SUMMARY.STLY_LY_COMSET_RATES_DATEWISE,
  });

  const channelStlyEndpoint = useSelectAPIEndpoint({
    standard: API_URL.MARKET_SEGMENT.CHANNEL_STLY,
    datewise: API_URL.MARKET_SEGMENT.CHANNEL_STLY_DATEWISE,
  });

  // Redux store state
  const defaultAsOfDateFromRTK = useSelector(
    (store) => store.headerProperty.defaultAsOfDate
  );
  const propertyDetailFromRTK = useSelector(
    (store) => store.headerProperty.propertyDetail
  );
  const asOfDateFromRTK = useSelector((store) => store.headerProperty.asOfDate);
  const activePropertyCode =
    para?.propertyCode ||
    propertyCode ||
    propertyDetailFromRTK?.propertycode ||
    "";

  const rawAsOf =
    para?.asOfDate ||
    asOfDateFromRTK ||
    defaultAsOfDateFromRTK ||
    new Date();

  const activeAsOfDate = moment(rawAsOf).isValid()
    ? moment(rawAsOf).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");

  const activeCapacity =
    parseInt(
      propertyDetailFromRTK?.revenuconfig?.[0]?.totalrooms,
      10
    ) || 150;

  const activeHotelName =
    propertyDetailFromRTK?.propertyname ||
    para?.propertyDetail?.propertyname ||
    activePropertyCode ||
    "Hotel";
  const isApiMode = api !== 0;
  const activeWidgetName = para?.widget_name || para?.widgetName || "promoradar";
  const activePropertyId =
    propertyDetailFromRTK?.propertyid ||
    para?.propertyDetail?.propertyid ||
    "";
  const forecastTypeTerm = (
    propertyDetailFromRTK?.revenuconfig?.[0]?.forcasttype_term || "ema"
  ).toLowerCase();

  // Date horizon handling (2 months: start = asOfDate, end = asOfDate + 2 months)
  const getHorizonDates = useCallback((asOf) => {
    const mAsOf = moment(asOf).isValid() ? moment(asOf) : moment();
    const mStart = moment(mAsOf);
    const mEnd = moment(mAsOf).add(2, "months");
    const s = new Date(mStart.year(), mStart.month(), mStart.date(), 12, 0, 0);
    const e = new Date(mEnd.year(), mEnd.month(), mEnd.date(), 12, 0, 0);
    return { start: s, end: e };
  }, []);



  const [startDate, setStartDate] = useState(
    () => getHorizonDates(activeAsOfDate).start
  );
  const [endDate, setEndDate] = useState(
    () => getHorizonDates(activeAsOfDate).end
  );
  const [startRangeDate, setStartRangeDate] = useState(
    () => getHorizonDates(activeAsOfDate).start
  );
  const [endRangeDate, setEndRangeDate] = useState(
    () => getHorizonDates(activeAsOfDate).end
  );
  const [activePresetRange, setActivePresetRange] = useState(null);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  // Synchronize horizon when top header date changes
  const prevAsOfDateRef = useRef(activeAsOfDate);
  const lastFetchedKeyRef = useRef("");
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    if (prevAsOfDateRef.current !== activeAsOfDate) {
      prevAsOfDateRef.current = activeAsOfDate;
      const horizon = getHorizonDates(activeAsOfDate);
      setStartDate(horizon.start);
      setEndDate(horizon.end);
      setStartRangeDate(horizon.start);
      setEndRangeDate(horizon.end);
      setActivePresetRange(null);
    }
  }, [activeAsOfDate, getHorizonDates]);

  const [tblids] = useState(() => ({
    id1: commonService.GenerateGUID(),
  }));

  // Widget toolbar state
  const [widgetModal, setWidgetModal] = useState(false);
  const widgetDetailRef = useRef();
  const closeWidgetModal = () => setWidgetModal(false);

  // Loading state & fetch cache
  const [loading, setLoading] = useState(api !== 0);
  const apiDataCacheRef = useRef(new Map());

  // Clear cache and reset fetch tracking on property change
  useEffect(() => {
    apiDataCacheRef.current.clear();
    lastFetchedKeyRef.current = "";
  }, [activePropertyCode]);

  // Ingested data payload
  const [dataPayload, setDataPayload] = useState(() => {
    try {






      const aligned = buildAlignedData(sampleData, activeAsOfDate, startDate, endDate, { name: activeHotelName, capacity: activeCapacity }); return ingestData(aligned);
    } catch (e) {
      console.error("PromoRadar init error:", e);
      return ingestData(sampleData);
    }
  });

  // Config payload
  const [configPayload, setConfigPayload] = useState(() => sampleConfig);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  // Discovered Channels persistent reference (collects all unique channels dynamically from live APIs: channel_stly and reservation_data)
  const discoveredChannelsRef = useRef(new Set());

  // Widget Configuration API Fetcher (api/widget/get_widget_config)
  const fetchWidgetConfig = useCallback(async () => {
    if (api === 0 || !activePropertyCode) return;
    setIsConfigLoading(true);
    try {
      const res = await apiCall(
        {
          method: "POST",
          url: API_URL.BASE_API_URL.SnapShot + API_URL.WIDGET_CONFIG.GET,
          body: {
            propertyCode: activePropertyCode,
            widget_name: activeWidgetName,
          },
        },
        false
      );

      const raw = res?.data?.data || res?.data || res;
      let cfg = raw?.config_json || raw;
      if (typeof cfg === "string") {
        try {
          cfg = JSON.parse(cfg);
        } catch {
          // ignore parsing error
        }
      }

      if (cfg && typeof cfg === "object" && (cfg.settings || cfg.hurdleTiers)) {
        setConfigPayload((prev) => {
          const baseConfig = {
            ...prev,
            ...cfg,
            settings: {
              ...(prev?.settings || {}),
              ...(cfg.settings || {}),
            },
            hurdleTiers:
              Array.isArray(cfg.hurdleTiers) && cfg.hurdleTiers.length > 0
                ? cfg.hurdleTiers
                : prev?.hurdleTiers || [],
            channels:
              Array.isArray(cfg.channels) && cfg.channels.length > 0
                ? cfg.channels
                : prev?.channels || [],
            promotionNames:
              Array.isArray(cfg.promotionNames) && cfg.promotionNames.length > 0
                ? cfg.promotionNames
                : prev?.promotionNames || [],
            verdicts:
              Array.isArray(cfg.verdicts) && cfg.verdicts.length > 0
                ? cfg.verdicts
                : prev?.verdicts || [],
            leakRules:
              Array.isArray(cfg.leakRules) && cfg.leakRules.length > 0
                ? cfg.leakRules
                : prev?.leakRules || [],
          };
          return mergeChannelsWithConfig(
            baseConfig,
            Array.from(discoveredChannelsRef.current)
          );
        });
      }
    } catch (err) {
      console.error("Failed to fetch widget config from API:", err);
    } finally {
      setIsConfigLoading(false);
    }
  }, [activePropertyCode, activeWidgetName, api]);

  // API Data fetcher
  const fetchPromoRadarData = useCallback(async (sDate, eDate) => {
    const alignedSample = buildAlignedData(sampleData, activeAsOfDate, sDate, eDate, {
      name: activeHotelName,
      capacity: activeCapacity,
    });

    if (api === 0) {
      setDataPayload(ingestData(alignedSample));
      return;
    }

    const formattedStart = commonService.getDateInDBFormat(sDate);
    const formattedEnd = commonService.getDateInDBFormat(eDate);
    const cacheKey = `${activePropertyCode}_${activeAsOfDate}_${formattedStart}_${formattedEnd}`;

    if (apiDataCacheRef.current.has(cacheKey)) {
      setDataPayload(apiDataCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const currentReqId = ++latestRequestIdRef.current;
    setLoading(true);

    try {
      // Concurrent API calls for essential endpoints:
      // 1. snp_ds_otb -> OnTheBook, LeftToSell, ADR
      // 2. dbd_forecast -> rms
      // 3. dbd_rates -> stlyotb
      // 4. rate_shop_list_asofdate -> Rate (BAR)
      // 5. channel_stly -> channel breakdown
      // 6. reservation_data -> detailed reservation bookings and channels
      const [
        orgRes,
        forecastRes,
        stlyRes,
        rateShopRes,
        channelStlyRes,
        reservationRes,
      ] = await Promise.allSettled([
        apiCall({
          method: "POST",
          url: API_URL.BASE_API_URL.SnapShot + API_URL.DAILY_SUMMARY.ORG,
          body: {
            AsOfDate: activeAsOfDate,
            startDate: formattedStart,
            endDate: formattedEnd,
            propertyCode: activePropertyCode,
          },
        }, false),
        apiCall({
          method: "POST",
          url: API_URL.BASE_API_URL.SnapShot + API_URL.DAILY_SUMMARY.PRICING_FORECAST,
          body: {
            AsOfDate: activeAsOfDate,
            startDate: formattedStart,
            endDate: formattedEnd,
            propertyid: activePropertyId,
            propertyCode: activePropertyCode,
            ForcastType: forecastTypeTerm,
          },
        }, false),
        apiCall({
          method: "POST",
          url: API_URL.BASE_API_URL.SnapShot + stlyComsetRatesEndpoint,
          body: {
            AsOfDate: activeAsOfDate,
            startDate: formattedStart,
            endDate: formattedEnd,
            propertyid: activePropertyId,
            propertyCode: activePropertyCode,
          },
        }, false),
        apiCall({
          method: "POST",
          url: API_URL.BASE_API_URL.SnapShot + API_URL.DAILY_SUMMARY.RATESHOP_LIST,
          body: {
            AsOfDate: activeAsOfDate,
            propertyCode: activePropertyCode,
            Channel: "",
            startDate: formattedStart,
            endDate: formattedEnd,
            propertyid: activePropertyId,
          },
        }, false),
        apiCall({
          method: "POST",
          url: API_URL.BASE_API_URL.SnapShot + channelStlyEndpoint,
          body: {
            AsOfDate: activeAsOfDate,
            startDate: formattedStart,
            endDate: formattedEnd,
            propertyid: activePropertyId,
            propertyCode: activePropertyCode,
          },
        }, false),
        apiCall({
          method: "POST",
          url: API_URL.BASE_API_URL.SnapShot + API_URL.YIELD_RADAR.RESERVATION_DATA,
          body: {
            AsOfDate: activeAsOfDate,
            startDate: formattedStart,
            endDate: formattedEnd,
            propertycode: activePropertyCode,
          },
        }, false),
      ]);

      // If a newer request was triggered while fetching, ignore this stale response
      if (currentReqId !== latestRequestIdRef.current) {
        return;
      }

      const extractList = (res) => {
        if (!res || res.status !== "fulfilled" || !res.value) return [];
        const val = res.value;
        if (Array.isArray(val)) return val;
        if (Array.isArray(val?.data)) return val.data;
        if (Array.isArray(val?.data?.data)) return val.data.data;
        try {
          const parsed = apiResponse(false, val);
          if (Array.isArray(parsed?.data)) return parsed.data;
          if (Array.isArray(parsed?.data?.data)) return parsed.data.data;
        } catch {
          // ignore
        }
        return [];
      };

      const toDateKey = (val) => {
        if (!val) return null;
        if (typeof val === "string") {
          const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
          if (m) return m[1];
        }
        const mom = moment(val);
        return mom.isValid() ? mom.format("YYYY-MM-DD") : null;
      };

      const orgDataList = extractList(orgRes);
      const forecastList = extractList(forecastRes);
      const stlyList = extractList(stlyRes);
      const rateShopList = extractList(rateShopRes);
      const channelStlyList = extractList(channelStlyRes);
      const reservationList = extractList(reservationRes);

      if (orgDataList.length > 0 || forecastList.length > 0 || stlyList.length > 0 || rateShopList.length > 0) {
        const dateMap = {};

        // 1. snp_ds_otb: OnTheBook, LeftToSell, ADR
        orgDataList.forEach((item) => {
          const rawDate = item?.Date || item?.date || item?.Dates || item?.dates;
          const dStr = toDateKey(rawDate);
          if (!dStr) return;
          const otbRoomsVal = parseFloat(item?.OnTheBook ?? item?.onTheBook ?? item?.OTB ?? item?.otb ?? 0);
          const otbRevVal = parseFloat(item?.REV ?? item?.rev ?? item?.Revenue ?? item?.revenue ?? 0);
          const adrVal = parseFloat(item?.ADR ?? item?.adr ?? 0);
          const ltsVal = parseFloat(item?.LeftToSell ?? item?.leftToSell ?? item?.left_to_sell ?? 0);

          dateMap[dStr] = {
            d: dStr,
            otbRooms: otbRoomsVal,
            leftToSell: ltsVal,
            adr: adrVal,
            otbRev: otbRevVal,
            occ: parseFloat(item?.TotalOCCPercentage ?? item?.totalOCCPercentage ?? 0),
            OnTheBook: otbRoomsVal,
            LeftToSell: ltsVal,
            ADR: adrVal,
          };
        });

        // 2. dbd_forecast: rms (Sys Forecast)
        forecastList.forEach((item) => {
          const rawDate = item?.Dates || item?.Date || item?.date || item?.dates;
          const dStr = toDateKey(rawDate);
          if (!dStr) return;
          if (!dateMap[dStr]) dateMap[dStr] = { d: dStr };
          dateMap[dStr].forecastOcc = parseFloat(item?.OccPerc || item?.TotalForecastOccupancy || item?.forecast_occ || 0);
          const fcRMS = item?.rms ?? item?.RMS ?? item?.ForecastRooms ?? item?.rooms;
          if (fcRMS != null) {
            const rmsNum = parseFloat(fcRMS);
            dateMap[dStr].forecastRooms = rmsNum;
            dateMap[dStr].RMS = rmsNum;
          }
          if (item?.["Optimal Bar"] != null) {
            const optBar = parseFloat(item["Optimal Bar"]);
            dateMap[dStr].optimalBar = optBar;
            if (!dateMap[dStr].bar) dateMap[dStr].bar = optBar;
          }
          // Only fallback ADR if not already provided by snp_ds_otb
          if (dateMap[dStr].ADR == null && item?.ADR != null) {
            dateMap[dStr].ADR = parseFloat(item?.ADR);
            if (!dateMap[dStr].adr) dateMap[dStr].adr = parseFloat(item?.ADR);
          }
        });

        // 3. dbd_rates: stlyotb (STLY)
        stlyList.forEach((item) => {
          const rawDate = item?.dates || item?.Date || item?.Dates || item?.date;
          const dStr = toDateKey(rawDate);
          if (!dStr) return;
          if (!dateMap[dStr]) dateMap[dStr] = { d: dStr };
          const stlyVal = parseFloat(
            item?.stlyotb ??
            item?.stly_otb ??
            item?.STLYOTB ??
            item?.stlyOtb ??
            item?.STLY_Rooms ??
            item?.rooms ??
            0
          );
          dateMap[dStr].stlyotb = stlyVal;
          dateMap[dStr].stlyRooms = stlyVal;

          dateMap[dStr].stlyRevenue = parseFloat(
            item?.stlyrevenue ??
            item?.stlyrev ??
            item?.stly_rev ??
            item?.STLYREV ??
            item?.stlyRev ??
            item?.STLY_Revenue ??
            item?.revenue ??
            0
          );

          dateMap[dStr].stlyOcc = parseFloat(
            item?.stlyocc ??
            item?.stly_occ ??
            item?.STLYOCC ??
            item?.STLY_OCC ??
            0
          );
        });

        // 4. rate_shop_list_asofdate: Rate (BAR)
        rateShopList.forEach((item) => {
          const rawDate = item?.CheckInDate || item?.checkInDate || item?.Date || item?.date || item?.Dates || item?.dates;
          const dStr = toDateKey(rawDate);
          if (!dStr) return;
          if (!dateMap[dStr]) dateMap[dStr] = { d: dStr };

          const isSelf =
            item?.IsSelf === true ||
            item?.isSelf === true ||
            item?.is_self === true ||
            item?.IsSelf === 1 ||
            item?.isSelf === 1 ||
            String(item?.IsSelf || item?.isSelf || item?.is_self).toLowerCase() === "true";

          const rate = parseFloat(item?.Rate ?? item?.rate ?? item?.bar ?? 0);
          if (rate > 0) {
            if (isSelf || !dateMap[dStr].Rate) {
              dateMap[dStr].Rate = rate;
              dateMap[dStr].bar = rate;
            }
          }
        });

        const apiChannelsSet = new Set();

        channelStlyList.forEach((item) => {
          const rawDate = item?.staydate || item?.StayDate || item?.date || item?.Date || item?.dates;
          const dStr = toDateKey(rawDate);
          const partnerRaw = String(
            item?.channelpartner ??
            item?.ChannelPartner ??
            item?.channelPartner ??
            item?.channel_partner ??
            item?.Channel_Partner ??
            item?.channel ??
            item?.Channel ??
            item?.channel_name ??
            item?.channelName ??
            item?.MarketSegment ??
            item?.market_segment ??
            item?.segment ??
            ""
          ).trim();
          if (!partnerRaw) return;

          // Standardize common naming while preserving every unique API channel
          const ch = cleanChannelName(partnerRaw);
          apiChannelsSet.add(ch);
          discoveredChannelsRef.current.add(ch);

          if (!dStr) return;
          if (!dateMap[dStr]) dateMap[dStr] = { d: dStr };
          if (!dateMap[dStr].channels) dateMap[dStr].channels = {};
          const curTy = parseFloat(item?.cy_roomnight ?? item?.CY_ROOMNIGHT ?? 0);
          const curStly = parseFloat(item?.ly_roomnight ?? item?.LY_ROOMNIGHT ?? 0);
          if (!dateMap[dStr].channels[ch]) {
            dateMap[dStr].channels[ch] = { ty: curTy, stly: curStly };
          } else {
            dateMap[dStr].channels[ch].ty += curTy;
            dateMap[dStr].channels[ch].stly += curStly;
          }
        });

        // Dynamic channels & bookings from reservation API (API_URL.YIELD_RADAR.RESERVATION_DATA)
        const dynamicBookings = [];
        reservationList.forEach((r) => {
          const chRaw = String(r?.channel ?? r?.Channel ?? "").trim();
          const ch = chRaw ? cleanChannelName(chRaw) : "";
          if (ch && ch.toLowerCase() !== "agoda") {
            apiChannelsSet.add(ch);
            discoveredChannelsRef.current.add(ch);
          }
          if (r?.arrival_date && r?.departure_date) {
            const arr = moment(r.arrival_date);
            const dep = moment(r.departure_date);
            const rateVal = parseFloat(r.nightly_rate ?? r.net_rate ?? 0);
            const curN = arr.clone();
            while (curN.isBefore(dep, "day")) {
              const sDateStr = curN.format("YYYY-MM-DD");
              dynamicBookings.push({
                confirmation: r.confirmation_no || String(Math.random()),
                promotionId: r.rate_plan_code || "",
                channel: ch || "UNMAPPED",
                stayDate: sDateStr,
                bookedDate: r.booking_date || sDateStr,
                roomType: r.room_type || "",
                rate: rateVal,
                status: r.status || "Confirmed",
                marketSegment: r.market_segment || "",
              });
              curN.add(1, "days");
            }
          }
        });

        // Also incorporate any distinct channels from RateShop (restricted to PMS property channels)
        rateShopList.forEach((item) => {
          const rawCh = String(
            item?.Channel ??
            item?.channel ??
            item?.ChannelPartner ??
            item?.channelpartner ??
            ""
          ).trim();
          if (rawCh && rawCh.toLowerCase() !== "all" && rawCh.toLowerCase() !== "null" && rawCh.toLowerCase() !== "undefined") {
            const ch = cleanChannelName(rawCh);
            const coreChannels = ["BOOKING.COM", "BRAND", "DIRECT", "EXPEDIA", "UNMAPPED"];
            if (coreChannels.includes(ch)) {
              apiChannelsSet.add(ch);
              discoveredChannelsRef.current.add(ch);
            }
          }
        });

        // DYNAMIC CHANNELS: Automatically map and synchronize all API channels into configPayload
        if (discoveredChannelsRef.current.size > 0) {
          setConfigPayload((prev) =>
            mergeChannelsWithConfig(prev, Array.from(discoveredChannelsRef.current))
          );
        }

        const liveNights = [];
        const cur = moment(sDate).startOf("day");
        const endLimit = moment(eDate).startOf("day");
        while (cur.isSameOrBefore(endLimit, "day")) {
          const dStr = cur.format("YYYY-MM-DD");
          const entry = dateMap[dStr] || {};
          const barRate = entry.optimalBar != null ? entry.optimalBar : (entry.bar || 0);

          const dayChannels = { ...(entry.channels || {}) };
          // Ensure all discovered channels exist with at least 0 counts if not in day
          discoveredChannelsRef.current.forEach((ch) => {
            if (!dayChannels[ch]) {
              dayChannels[ch] = { ty: 0, stly: 0 };
            }
          });

          liveNights.push({
            date: dStr,
            event: entry.event || entry.eventName || "",
            otbRooms: entry.otbRooms || 0,
            leftToSell: entry.leftToSell != null ? entry.leftToSell : 0,
            adr: entry.adr || 0,
            otbRevenue: entry.otbRev || (entry.otbRooms ? entry.otbRooms * (entry.adr || 0) : 0),
            stlyRooms: entry.stlyRooms || 0,
            stlyRevenue: entry.stlyRevenue || 0,
            forecastRooms: entry.forecastRooms || 0,
            bar: barRate,
            OnTheBook: entry.OnTheBook != null ? entry.OnTheBook : (entry.otbRooms || 0),
            LeftToSell: entry.LeftToSell != null ? entry.LeftToSell : (entry.leftToSell != null ? entry.leftToSell : 0),
            ADR: entry.ADR != null ? entry.ADR : (entry.adr || 0),
            RMS: entry.RMS != null ? entry.RMS : (entry.forecastRooms || 0),
            stlyotb: entry.stlyotb != null ? entry.stlyotb : (entry.stlyRooms || 0),
            Rate: entry.Rate != null ? entry.Rate : barRate,
            channels: (Object.keys(dayChannels).length > 0)
              ? dayChannels
              : (alignedSample.daily?.[liveNights.length]?.channels || {}),
          });
          cur.add(1, "days");
        }

        const activePromotions = (alignedSample.promotions && alignedSample.promotions.length > 0)
          ? alignedSample.promotions
          : (sampleData.promotions || []);

        const activeBookings = (dynamicBookings.length > 0)
          ? dynamicBookings
          : ((alignedSample.bookings && alignedSample.bookings.length > 0)
              ? alignedSample.bookings
              : (sampleData.bookings || []));

        const mergedPayload = {
          hotel: {
            name: activeHotelName || "Hotel",
            capacity: activeCapacity || 150,
            asOf: activeAsOfDate || "",
          },
          daily: liveNights.length > 0 ? liveNights : alignedSample.daily,
          promotions: activePromotions,
          campaigns: alignedSample.campaigns || [],
          bookings: activeBookings,
          channelAliases: alignedSample.channelAliases || {},
        };

        const finalIngested = ingestData(mergedPayload);
        if (discoveredChannelsRef.current) {
          discoveredChannelsRef.current.forEach((ch) => finalIngested.dataKeys.add(ch));
        }
        apiDataCacheRef.current.set(cacheKey, finalIngested);
        setDataPayload(finalIngested);
      } else {
        const sampleIngested = ingestData(alignedSample);
        apiDataCacheRef.current.set(cacheKey, sampleIngested);
        setDataPayload(sampleIngested);
      }
    } catch (err) {
      console.error("PromoRadar data fetch failed, fallback to aligned sample data:", err);
      setDataPayload(ingestData(alignedSample));
    } finally {
      if (currentReqId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [activeAsOfDate, activePropertyCode, activeHotelName, activeCapacity, api, activePropertyId, forecastTypeTerm, channelStlyEndpoint, stlyComsetRatesEndpoint]);

  // Date range picker handlers
  const handleDateChange = (dates) => {
    if (api === 0) return;
    const [start, end] = dates;
    setStartRangeDate(start);
    setEndRangeDate(end);
    setActivePresetRange(null);
  };

  const handlePresetChange = (start, end, type) => {
    if (api === 0) return;
    setStartRangeDate(start);
    setEndRangeDate(end);
    setStartDate(start);
    setEndDate(end);
  };

  const handleApply = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (api === 0) return;
    if (startRangeDate && endRangeDate) {
      setStartDate(startRangeDate);
      setEndDate(endRangeDate);
      setOpenDatePicker(false);
    }
  };

  const handleCancel = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (api === 0) return;
    setStartRangeDate(startDate);
    setEndRangeDate(endDate);
    setOpenDatePicker(false);
    setActivePresetRange(null);
  };

  const handleReset = () => {
    const horizon = getHorizonDates(activeAsOfDate);
    setStartDate(horizon.start);
    setEndDate(horizon.end);
    setStartRangeDate(horizon.start);
    setEndRangeDate(horizon.end);
    setActivePresetRange(null);
    if (api === 0) {
      const aligned = buildAlignedData(sampleData, activeAsOfDate, horizon.start, horizon.end, {
        name: activeHotelName,
        capacity: activeCapacity,
      });
      setDataPayload(ingestData(aligned));
    }
  };

  // Auto-fetch saved widget configuration from server on mount / property change
  useEffect(() => {
    fetchWidgetConfig();
  }, [fetchWidgetConfig]);

  useEffect(() => {
    // If activeAsOfDate changed, wait for the horizon effect above to synchronize startDate/endDate
    if (prevAsOfDateRef.current !== activeAsOfDate) {
      return;
    }

    const startStr = commonService.getDateInDBFormat(startDate);
    const endStr = commonService.getDateInDBFormat(endDate);
    const requestKey = `${activePropertyCode}_${activeAsOfDate}_${startStr}_${endStr}`;

    // STRICT DEDUPLICATION: If this exact dataset was already fetched or is fetching, do not re-fetch!
    if (lastFetchedKeyRef.current === requestKey) {
      return;
    }

    if (api === 0) {
      lastFetchedKeyRef.current = requestKey;
      const aligned = buildAlignedData(sampleData, activeAsOfDate, startDate, endDate, {
        name: activeHotelName,
        capacity: activeCapacity,
      });
      setDataPayload(ingestData(aligned));
      return;
    }

    lastFetchedKeyRef.current = requestKey;
    fetchPromoRadarData(startDate, endDate);
  }, [startDate, endDate, activeAsOfDate, activePropertyCode, api, fetchPromoRadarData, activeHotelName, activeCapacity]);
  // Main Radar computation
  const radarData = useMemo(() => {
    return computePromoRadar(dataPayload, configPayload);
  }, [dataPayload, configPayload]);

  // UI state
  const [showConfig, setShowConfig] = useState(false);
  const [isConfigDirty, setIsConfigDirty] = useState(false);

  // Drawer state
  const [drawerState, setDrawerState] = useState({
    isOpen: false,
    type: null, // 'night' or 'promo'
    nightIndex: null,
    promoId: null,
  });

  // Brief modal state
  const [briefModal, setBriefModal] = useState({
    show: false,
    campaign: null,
  });

  // Leak queueing & filters
  const [leakFilter, setLeakFilter] = useState({ rule: "", promo: "" });
  const [queuedLeaks, setQueuedLeaks] = useState(new Set());
  const [uncheckedLeaks, setUncheckedLeaks] = useState(new Set());

  // Calendar toolbar & interactive state
  const [calFilter, setCalFilter] = useState({ query: "", ch: "" });
  const [searchInput, setSearchInput] = useState("");
  const calScrollRef = useRef(null);
  const calHeadScrollRef = useRef(null);
  const perfScrollRef = useRef(null);
  const scrollTimerRef = useRef(null);

  // Debounce search input (250ms) to ensure butter-smooth typing without lag
  useEffect(() => {
    const timer = setTimeout(() => {
      setCalFilter((prev) => (prev.query === searchInput ? prev : { ...prev, query: searchInput }));
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const scrollToNight = (k) => {
    if (calScrollRef.current) {
      const colWidth = 19;
      calScrollRef.current.scrollTo({
        left: k * colWidth,
        behavior: "smooth",
      });
    }
  };

  // Tooltip via direct DOM ref (0 re-renders, 0ms lag)
  const tooltipRef = useRef(null);

  const showTooltip = useCallback((html, rect) => {
    if (!tooltipRef.current) return;
    const cellCenterX = rect.left + rect.width / 2;
    const isTooCloseToTop = rect.top < 160;
    const clampedX = Math.max(140, Math.min(window.innerWidth - 140, cellCenterX));
    const arrowLeftPercent = Math.max(10, Math.min(90, 50 + ((cellCenterX - clampedX) / 280) * 100));

    const tip = tooltipRef.current;
    tip.innerHTML = html;
    tip.style.left = `${clampedX}px`;
    tip.style.top = isTooCloseToTop ? `${rect.bottom + 8}px` : `${rect.top - 8}px`;
    tip.style.setProperty("--arrow-left", `${arrowLeftPercent}%`);
    tip.className = `PromoRadar-tip show ${isTooCloseToTop ? "placement-bottom" : "placement-top"}`;
  }, []);

  const hideTooltip = useCallback(() => {
    if (!tooltipRef.current) return;
    tooltipRef.current.className = "PromoRadar-tip";
  }, []);

  // Toast using app in-build toastMessage
  const showToast = useCallback((msg, type = "success") => {
    if (type === "error") {
      errorToastMessage(msg);
    } else if (type === "info") {
      infoToastMessage(msg);
    } else {
      successToastMessage(msg);
    }
  }, []);

  const handlePreviewApply = useCallback((previewConfig) => {
    setConfigPayload(previewConfig);
    setIsConfigDirty(true);
  }, []);

  const handleSaveConfig = useCallback(
    async (newConfig) => {
      // Ensure all discovered channels remain intact in saved payload
      const fullConfig = mergeChannelsWithConfig(
        newConfig,
        Array.from(discoveredChannelsRef.current)
      );
      if (api !== 0 && activePropertyCode) {
        const res = await apiCall(
          {
            method: "POST",
            url: API_URL.BASE_API_URL.SnapShot + API_URL.WIDGET_CONFIG.SET,
            body: {
              propertyCode: activePropertyCode,
              widget_name: activeWidgetName,
              config_json: fullConfig,
            },
          },
          false
        );
        if (res?.data?.status === false || res?.status === false) {
          const errMsg =
            res?.data?.message || res?.message || "Failed to save configuration";
          showToast(errMsg, "error");
          throw new Error(errMsg);
        }
        setConfigPayload(fullConfig);
        setIsConfigDirty(false);
        showToast("Configuration saved successfully");
      } else {
        setConfigPayload(fullConfig);
        setIsConfigDirty(false);
        showToast("Configuration saved locally");
      }
    },
    [activePropertyCode, activeWidgetName, api, showToast]
  );

  const openNight = useCallback((k) => {
    setDrawerState({
      isOpen: true,
      type: "night",
      nightIndex: k,
      promoId: null,
    });
  }, []);

  const openPromo = useCallback((id) => {
    setDrawerState({
      isOpen: true,
      type: "promo",
      nightIndex: null,
      promoId: id,
    });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const openBrief = (campaign) => {
    setBriefModal({
      show: true,
      campaign,
    });
  };

  // Filtered leaks list
  const filteredLeaks = useMemo(() => {
    return (radarData.leaks || []).filter((l) => {
      if (leakFilter.rule && l.code !== leakFilter.rule) return false;
      if (leakFilter.promo && radarData.pr[l.i]?.id !== leakFilter.promo)
        return false;
      return true;
    });
  }, [radarData.leaks, radarData.pr, leakFilter]);

  // Selected leaks available to queue
  const selectableLeaks = useMemo(() => {
    return filteredLeaks
      .slice(0, 40)
      .filter((l) => !queuedLeaks.has(l.key) && !uncheckedLeaks.has(l.key));
  }, [filteredLeaks, queuedLeaks, uncheckedLeaks]);

  const handleQueueLeaks = () => {
    if (!selectableLeaks.length) return;
    setQueuedLeaks((prev) => {
      const next = new Set(prev);
      selectableLeaks.forEach((l) => next.add(l.key));
      return next;
    });
    showToast(`${selectableLeaks.length} fixes sent to the RM task list`);
  };

  // Color constants
  const CELL_BG = {
    OK: "var(--c-ok)",
    L1: "var(--c-l1)",
    L2: "var(--c-l2)",
    L3: "var(--c-l3)",
  };

  const {
    config,
    nights,
    pr,
    code,
    leak,
    leaks,
    camps,
    chColor,
    directCh,
    resInfo,
    getLeakAction,
  } = radarData;

  const totalAtRisk = leaks.reduce((t, l) => t + l.amt, 0);
  const livePromos = pr.filter((p) => p.status === "Live");
  const promoRevenue = livePromos.reduce(
    (t, p) => t + (p.perf?.vis ? 0 : p.perf?.H || 0),
    0,
  );
  const netImpact = livePromos.reduce(
    (t, p) => t + (p.perf?.AC || 0),
    0,
  );
  const newCampsCount = camps.filter((c) => !c.covered).length;
  const coveredCampsCount = camps.filter((c) => c.covered).length;

  // Horizontal leak bars
  const leakBarRows = useMemo(() => {
    const rows = pr
      .filter((p) => p.leak$ > 0)
      .sort((a, b) => b.leak$ - a.leak$);
    const maxVal = Math.max(...rows.map((p) => p.leak$), 1);

    const byCode = (p, c) => {
      const pIdx = pr.indexOf(p);
      return (leak[pIdx] || []).reduce(
        (t, v, k) => t + (code[pIdx]?.[k] === c ? v : 0),
        0,
      );
    };

    return {
      rows: rows.map((p) => {
        const a = byCode(p, "L1");
        const b = byCode(p, "L2");
        const c = byCode(p, "L3");
        return {
          id: p.id,
          name: p.name,
          label: `${p.id} ${p.name}`,
          val: fmt$(p.leak$),
          segs: [
            { x: 0, w: a, c: "var(--c-l1)" },
            { x: a, w: b, c: "var(--c-l2)" },
            { x: a + b, w: c, c: "var(--c-l3)" },
          ],
        };
      }),
      max: maxVal,
    };
  }, [pr, leak, code]);

  // Calendar helpers
  const DOW_CHARS = ["S", "M", "T", "W", "T", "F", "S"];

  const monthBuckets = useMemo(() => {
    const list = [];
    (nights || []).forEach((x, k) => {
      const d = dObj(x.d);
      const mKey = `${d.getFullYear()}-${d.getMonth()}`;
      const mName = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      const mShort = d.toLocaleDateString("en-US", { month: "short" });
      if (!list.length || list[list.length - 1].mKey !== mKey) {
        list.push({ mKey, mName, mShort, index: k, count: 1 });
      } else {
        list[list.length - 1].count++;
      }
    });
    return list;
  }, [nights]);

  const allChannels = useMemo(() => {
    const fromPromos = (pr || []).map((p) => p.ch).filter(Boolean);
    const promoChannels = Array.from(new Set(fromPromos));
    if (promoChannels.length > 0) {
      return promoChannels;
    }
    const fromConfig = (configPayload?.channels || []).map((c) => c.name).filter(Boolean);
    return Array.from(new Set(fromConfig));
  }, [pr, configPayload]);

  const channelCounts = useMemo(() => {
    const counts = {};
    (pr || []).forEach((p) => {
      const ch = p.ch || "Unknown";
      counts[ch] = (counts[ch] || 0) + 1;
    });
    return counts;
  }, [pr]);

  const displayedPromos = useMemo(() => {
    return (pr || []).filter((p) => {
      if (calFilter.ch && p.ch !== calFilter.ch) return false;
      if (calFilter.query) {
        const q = calFilter.query.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.id.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [pr, calFilter]);

  // React Virtualization for Calendar Grid (renders ~25 rows instead of 539)
  const calVirtual = useVirtualList({
    totalCount: displayedPromos.length,
    itemHeight: 38,
    overscan: 18,
    containerRef: calScrollRef,
  });

  const visiblePromos = useMemo(() => {
    return displayedPromos.slice(calVirtual.startIndex, calVirtual.endIndex + 1);
  }, [displayedPromos, calVirtual.startIndex, calVirtual.endIndex]);

  // React Virtualization for Performance Table (renders ~20 rows instead of 539)
  const perfVirtual = useVirtualList({
    totalCount: (pr || []).length,
    itemHeight: 49,
    overscan: 14,
    containerRef: perfScrollRef,
  });

  const visiblePerfPromos = useMemo(() => {
    return (pr || []).slice(perfVirtual.startIndex, perfVirtual.endIndex + 1);
  }, [pr, perfVirtual.startIndex, perfVirtual.endIndex]);

  const handleBodyScroll = useCallback((e) => {
    calVirtual.handleScroll();
    const el = e.currentTarget;
    if (tooltipRef.current) tooltipRef.current.className = "PromoRadar-tip";

    if (!el.classList.contains("is-scrolling")) {
      el.classList.add("is-scrolling");
    }
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      el.classList.remove("is-scrolling");
    }, 100);

    const sl = el.scrollLeft;
    if (calHeadScrollRef.current && calHeadScrollRef.current.scrollLeft !== sl) {
      calHeadScrollRef.current.scrollLeft = sl;
    }
  }, [calVirtual]);

  // Precomputed O(1) Map for matrix cell lookup - eliminates ~18 million comparisons per render
  const promoLookup = useMemo(() => {
    const map = new Map();
    (pr || []).forEach((p, pIdx) => {
      map.set(p.id, {
        p,
        pIdx,
        codeRow: code[pIdx] || [],
        leakRow: leak[pIdx] || [],
      });
    });
    return map;
  }, [pr, code, leak]);

  // Calendar matrix event delegation (eliminates 66,000+ per-cell closures)
  const handleMatrixMouseOver = useCallback((e) => {
    const target = e.target.closest(".promo-cell");
    if (!target) return;
    const k = Number(target.getAttribute("data-night"));
    const pid = target.getAttribute("data-pid");
    const cellCode = target.getAttribute("data-code");
    const leakAmt = Number(target.getAttribute("data-leak") || 0);

    const x = nights[k];
    const pData = promoLookup.get(pid);
    const p = pData?.p;
    if (!x || !p) return;

    const rect = target.getBoundingClientRect();
    const html = `
      <div style="font-weight:700;font-size:13px;margin-bottom:2px">${p.id} · ${p.name}</div>
      <div style="font-size:11.5px;color:#d7e0fe;margin-bottom:6px">${p.ch} · ${p.status}${p.disc ? `, ${pct(p.disc)} off` : ""}</div>
      <div style="border-top:1px solid rgba(255,255,255,0.2);padding-top:4px;font-size:12px">
        <b>${dmd(x.d)}</b> (${x.dba} days out)<br/>
        Forecast: <b>${pct(x.fc)}</b> (${x.diag})<br/>
        ${cellCode === "OK"
          ? '<span style="color:#4ade80">● Safe (Bookable)</span>'
          : cellCode
            ? `<span style="color:#f87171">● ${LEAK_NAME[cellCode]}</span>: <b>${fmt$(leakAmt)}</b> at risk`
            : '<span style="color:#94a3b8">○ Not bookable on this date</span>'
        }
      </div>
      <div style="margin-top:4px;font-size:10.5px;color:#cbd5e1">Click to view night drilldown</div>
    `;
    showTooltip(html, rect);
  }, [nights, promoLookup, showTooltip]);

  const handleMatrixMouseOut = useCallback((e) => {
    const target = e.target.closest(".promo-cell");
    if (!target) return;
    if (e.relatedTarget && target.contains(e.relatedTarget)) return;
    hideTooltip();
  }, [hideTooltip]);

  const handleMatrixClick = useCallback((e) => {
    const target = e.target.closest(".promo-cell");
    if (!target) return;
    const k = Number(target.getAttribute("data-night"));
    if (!isNaN(k)) {
      openNight(k);
    }
  }, [openNight]);

  const currentHotelInfo = useMemo(() => {
    return {
      name:
        propertyDetailFromRTK?.propertyname ||
        para?.propertyDetail?.propertyname ||
        radarData?.hotelInfo?.name ||
        activePropertyCode ||
        "Hotel",
      capacity:
        parseInt(
          propertyDetailFromRTK?.totalrooms || para?.propertyDetail?.totalrooms,
          10
        ) ||
        radarData?.hotelInfo?.capacity ||
        150,
      asOf:
        activeAsOfDate ||
        radarData?.hotelInfo?.asOf ||
        moment().format("YYYY-MM-DD"),
    };
  }, [
    propertyDetailFromRTK,
    para,
    activePropertyCode,
    activeAsOfDate,
    radarData?.hotelInfo,
  ]);

  return (
    <div
      className={`widget-container yieldradar-widget-main snapshotrow ${api === 0 ? "yieldradar-disabled" : ""}`}
      style={{
        position: "relative",
        padding: "0 5px",
        ...(api === 0 ? { pointerEvents: "none", userSelect: "none" } : {}),
      }}
      rowtitle="Promo Radar"
    >
      {/* Top Menu Buttons for EMA Widget */}
      {api !== 0 && (
        <div className="gap-1 widget-container-menu">
          <Button className="heading-menu-btns" type="button" title="Info">
            <img
              id={commonService.generateGlobalClickTrackerID(
                "PromoRadar",
                "infoIcon",
                propertyDetailFromRTK?.propertyid || activePropertyCode
              )}
              src={infoIcon}
              alt="info"
              onClick={() => {
                if (api === 0) return;
                widgetDetailRef.current = {
                  widgetdetails: "",
                  usernote: "",
                  compName: compName?.PromoRadar || "PromoRadar",
                  widgetId: para?.widgetId,
                };
                setWidgetModal(true);
              }}
            />
          </Button>

          <Button className="heading-menu-btns" type="button" title="Send Mail">
            <img
              id={commonService.generateGlobalClickTrackerID(
                "PromoRadar",
                "emailIcon",
                propertyDetailFromRTK?.propertyid || activePropertyCode
              )}
              src={emailIcon}
              alt="emailIcon"
              onClick={() => {
                if (api === 0) return;
                commonService.fnSendWidgetMail(
                  tblids.id1,
                  "Promo Radar",
                  snapshotId,
                  defaultAsOfDateFromRTK,
                  "",
                  para?.widgetId
                );
              }}
            />
          </Button>

          <Button className="heading-menu-btns" type="button" title="Download">
            <img
              id={commonService.generateGlobalClickTrackerID(
                "PromoRadar",
                "downloadIcon",
                propertyDetailFromRTK?.propertyid || activePropertyCode
              )}
              src={downloadIcon}
              alt="download"
              import={tblids.id1}
              onClick={() => {
                if (api === 0) return;
                commonService.fnExportInCSV(
                  tblids.id1,
                  `${activePropertyCode || "Property"}-Promo Radar-${moment(new Date()).format("MMDDYYYY")}`
                );
              }}
            />
          </Button>
        </div>
      )}

      <div className="w-100 position-relative" style={{ minHeight: "500px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255, 255, 255, 0.65)",
              zIndex: 999,
            }}
          >
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Loader />
            </div>
          </div>
        )}
        <div className="PromoRadar" id="promoRadarTable">
        <div className="wrap">
          {/* EMA Range Picker & Controls */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
            <div className="groupiq-date-controls" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <div className="groupiq-date-input">
                <DatePicker
                  id={commonService.generateGlobalClickTrackerID(
                    "PromoRadar",
                    "range-date-picker",
                    activePropertyCode,
                  )}
                  portalId="datepicker-root"
                  className="groupiq-datepicker"
                  onChange={handleDateChange}
                  startDate={startRangeDate}
                  endDate={endRangeDate}
                  selectsRange
                  monthsShown={2}
                  open={api === 0 ? false : openDatePicker}
                  disabled={api === 0}
                  onClickOutside={() => setOpenDatePicker(false)}
                  onInputClick={() => {
                    if (api === 0) return;
                    setOpenDatePicker(true);
                  }}
                  shouldCloseOnSelect={false}
                  placeholderText="MM/DD/YYYY - MM/DD/YYYY"
                >
                  <div className="w-100" style={{ display: "inline-block" }}>
                    <CustomRangePicker
                      id={commonService.generateGlobalClickTrackerID(
                        "PromoRadar",
                        "occ-date-rangepicker",
                        activePropertyCode,
                      )}
                      activePreset={activePresetRange}
                      setActivePreset={setActivePresetRange}
                      setOpenDatePicker={setOpenDatePicker}
                      onChange={(start, end, type) => {
                        if (api === 0) return;
                        handlePresetChange(start, end, type);
                      }}
                    />
                    <div className="content-inside-calendar">
                      <p>
                        {startRangeDate
                          ? commonService.getDateInFormat(startRangeDate)
                          : ""}
                        {startRangeDate && endRangeDate ? " - " : ""}
                        {endRangeDate
                          ? commonService.getDateInFormat(endRangeDate)
                          : ""}
                      </p>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          id={commonService.generateGlobalClickTrackerID(
                            "PromoRadar",
                            "occ-date-cancel-btn",
                            activePropertyCode,
                          )}
                          className="secondary-btn"
                          disabled={api === 0}
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          id={commonService.generateGlobalClickTrackerID(
                            "PromoRadar",
                            "occ-date-apply-btn",
                            activePropertyCode,
                          )}
                          className="primary-btn"
                          disabled={api === 0 || !startRangeDate || !endRangeDate}
                          onClick={handleApply}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </DatePicker>
              </div>
              <Button
                id={commonService.generateGlobalClickTrackerID(
                  "PromoRadar",
                  "reset",
                  activePropertyCode,
                )}
                className="default-btn primary-btn promoradar-ctrl-btn"
                type="button"
                disabled={api === 0}
                onClick={handleReset}
                style={{
                  height: "30px",
                  padding: "0 18px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  fontWeight: 600,
                  borderRadius: "5px",
                  transition: "all 0.2s ease",
                  ...(api === 0
                    ? {
                        backgroundColor: "#f1f5f9",
                        color: "#94a3b8",
                        borderColor: "#cbd5e1",
                        cursor: "not-allowed",
                        opacity: 0.85,
                      }
                    : {
                        backgroundColor: "var(--blue, #3565fc)",
                        color: "#ffffff",
                        borderColor: "var(--blue, #3565fc)",
                        cursor: "pointer",
                      }),
                }}
              >
                RESET
              </Button>
              <Button
                id={commonService.generateGlobalClickTrackerID(
                  "PromoRadar",
                  "config",
                  activePropertyCode,
                )}
                disabled={api === 0}
                className="default-btn primary-btn promoradar-ctrl-btn"
                type="button"
                onClick={() => {
                  if (api !== 0) {
                    fetchWidgetConfig();
                  }
                  setShowConfig(true);
                }}
                style={{
                  height: "30px",
                  padding: "0 14px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  borderRadius: "5px",
                  fontWeight: 500,
                  gap: "6px",
                  transition: "all 0.2s ease",
                  ...(api === 0
                    ? {
                        backgroundColor: "#f1f5f9",
                        color: "#94a3b8",
                        borderColor: "#cbd5e1",
                        cursor: "not-allowed",
                        opacity: 0.85,
                      }
                    : {
                        backgroundColor: "var(--blue, #3565fc)",
                        color: "#ffffff",
                        borderColor: "var(--blue, #3565fc)",
                        cursor: "pointer",
                      }),
                }}
              >
                Configuration
                {isConfigDirty && <span className="cfg-dot" />}
              </Button>
              {/* {loading && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#1d4ed8",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    padding: "4px 10px",
                    borderRadius: "16px",
                    lineHeight: 1,
                  }}
                >
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ width: "11px", height: "11px", borderWidth: "1.5px" }}
                  />
                  Syncing live data...
                </div>
              )} */}
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {!isApiMode && (
                <span className="sample-tag" title="Built-in sample dataset active">
                  Sample data
                </span>
              )}
              {resInfo?.total > 0 && (
                <span className="reason">
                  {resInfo.matched}/{resInfo.total} reservations matched
                </span>
              )}
            </div>
          </div>

          {/* HERO */}
          <div className="hero">
            <div>
              <h2 className="headline">
                <span className="amt">{fmt$(totalAtRisk)}</span> is at risk on{" "}
                {leaks.length} promotion nights that should not be on sale.
              </h2>
              <p>
                {pr.filter((p) => p.leakN).length} of{" "}
                {pr.filter((p) => p.status !== "Paused").length} active promotions
                are still bookable on nights Yield Radar says will sell at full
                rate, or below the night's hurdle rate.
              </p>
            </div>
            <div className="kpis">
              <div className="kpi">
                <div className="k">Live promotions</div>
                <div className="v num">{livePromos.length}</div>
                <div className="d">
                  {pr.filter((p) => p.status === "Planned").length} planned,{" "}
                  {pr.filter((p) => p.status === "Paused").length} paused
                </div>
              </div>
              <div className="kpi">
                <div className="k">Promotion revenue</div>
                <div className="v num">{fmt$(promoRevenue)}</div>
                <div className="d">On the books, discounted plans</div>
              </div>
              <div className="kpi">
                <div className="k">Net impact</div>
                <div
                  className="v num"
                  style={{ color: netImpact < 0 ? "var(--l1)" : "inherit" }}
                >
                  {fmt$(netImpact)}
                </div>
                <div className="d">After cannibalization and fees</div>
              </div>
              <div className="kpi">
                <div className="k">New campaigns</div>
                <div className="v num">{newCampsCount}</div>
                <div className="d">{coveredCampsCount} covered by live promotions</div>
              </div>
            </div>
          </div>

          {/* PROMOTION CALENDAR */}
          <section className="panel" aria-label="Promotion calendar">
            <div className="phead">
              <div>
                <h2>Promotion calendar</h2>
                <p>
                  Every promotion on every night for the selected 2 months. Hover over any cell to see full diagnosis and revenue at risk. Select a cell to open that night, or a promotion name to open its drilldown.
                </p>
              </div>
              <div className="legend">
                <span>
                  <i style={{ background: "var(--c-ok)" }} />
                  Safe
                </span>
                <span>
                  <i style={{ background: "var(--c-l1)" }} />
                  Blackout night (L1)
                </span>
                <span>
                  <i style={{ background: "var(--c-l2)" }} />
                  Below hurdle (L2)
                </span>
                <span>
                  <i style={{ background: "var(--c-l3)" }} />
                  Stacking (L3)
                </span>
              </div>
            </div>

            {/* CALENDAR TOOLBAR: Filter by promo/channel & Quick Month Jumps */}
            <div className="cal-toolbar">
              <div className="cal-filters">
                <div className="cal-search-box">
                  <input
                    type="text"
                    className="cal-search"
                    placeholder="Filter promotions..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      title="Clear filter"
                      onClick={() => {
                        setSearchInput("");
                        setCalFilter((prev) => ({ ...prev, query: "" }));
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="ch-pills">
                  <button
                    type="button"
                    className={`ch-pill ${!calFilter.ch ? "active" : ""}`}
                    onClick={() => setCalFilter((prev) => ({ ...prev, ch: "" }))}
                  >
                    All ({pr.length})
                  </button>
                  {allChannels.map((ch) => {
                    const count = channelCounts[ch] || 0;
                    return (
                      <button
                        key={ch}
                        type="button"
                        className={`ch-pill ${calFilter.ch === ch ? "active" : ""}`}
                        onClick={() =>
                          setCalFilter((prev) => ({
                            ...prev,
                            ch: prev.ch === ch ? "" : ch,
                          }))
                        }
                      >
                        <span
                          className="dot"
                          style={{ background: chColor[ch] || "var(--primary)" }}
                        />
                        {ch} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="cal-jumps">
                <span className="jump-label">Jump to:</span>
                {monthBuckets.map((m) => (
                  <button
                    key={m.mName}
                    type="button"
                    className="jump-btn"
                    onClick={() => scrollToNight(m.index)}
                  >
                    {m.mShort}
                  </button>
                ))}
              </div>
            </div>

            <div className="cal">
              {/* Frozen Calendar Header */}
              <div className="cal-header-row pe-2">
                <div className="cal-header-names">
                  <div className="cal-row month-hdr-blank" />
                  <div className="cal-row hdr">Night / Date</div>
                  <div className="cal-row band">
                    <span>Blackout nights</span>
                  </div>
                </div>
                <div
                  className="cal-header-scroll"
                  ref={calHeadScrollRef}
                >
                  <div
                    className="grid grid-header"
                    style={{
                      gridTemplateRows: "20px 26px 28px",
                    }}
                  >
                    {nights.map((x, k) => {
                      const dateObj = dObj(x.d);
                      const isFirstOfMonth = dateObj.getDate() === 1 || k === 0;
                      const isWeekend =
                        dateObj.getDay() === 0 || dateObj.getDay() === 6;

                      return (
                        <React.Fragment key={x.d}>
                          {/* Row 1: Month tag header */}
                          <div
                            className={`cell month-hdr ${isFirstOfMonth ? "mon-start" : ""} ${isWeekend ? "weekend" : ""}`}
                            aria-hidden="true"
                          >
                            {isFirstOfMonth ? (
                              <span className="month-name">
                                {dateObj.toLocaleDateString("en-US", {
                                  month: "short",
                                })}
                              </span>
                            ) : null}
                          </div>

                          {/* Row 2: Day of Month & Day of Week header */}
                          <div
                            className={`cell hdrc ${isFirstOfMonth ? "mon-start" : ""} ${isWeekend ? "weekend" : ""}`}
                            aria-hidden="true"
                            title={`${dmd(x.d)} · ${x.diag}`}
                          >
                            <span className="dow-letter">
                              {DOW_CHARS[dateObj.getDay()]}
                            </span>
                            <span className="dom-num">{dateObj.getDate()}</span>
                          </div>

                          {/* Row 3: Blackout nights row cell */}
                          <button
                            className={`cell blackout-cell ${isFirstOfMonth ? "mon-start" : ""} ${isWeekend ? "weekend" : ""}`}
                            type="button"
                            aria-label={`${dmd(x.d)}, ${x.blackout ? "blackout" : "open"} night`}
                            onClick={() => openNight(k)}
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const html = `
                                <div style="font-weight:700;font-size:13px;margin-bottom:3px">${dmd(x.d)} (${x.dba} days out)</div>
                                <div style="font-size:12px;color:#d7e0fe;margin-bottom:4px">Diagnosis: <b>${x.diag}</b> · Forecast: <b>${pct(x.fc)}</b></div>
                                <div style="font-size:12px;padding-top:4px;border-top:1px solid rgba(255,255,255,0.2)">
                                  ${x.blackout ? '<span style="color:#f87171">● Blackout night</span> (full-rate expected, close discounts)' : '<span style="color:#4ade80">● Open night</span> for promotions'}
                                </div>
                                <div style="font-size:10.5px;color:#cbd5e1;margin-top:4px">Click to inspect night in drawer</div>
                              `;
                              showTooltip(html, rect);
                            }}
                            onMouseLeave={hideTooltip}
                          >
                            <span
                              className="cell-pill"
                              style={{
                                background: x.blackout
                                  ? "var(--c-l1)"
                                  : "transparent",
                                opacity: x.blackout ? 1 : 0,
                              }}
                            />
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Viewport-bounded Scrollable Body */}
              <div
                className="cal-viewport-body"
                ref={calScrollRef}
                onScroll={handleBodyScroll}
              >
                <div className="cal-body-content">
                  {/* Left promo names column with React Virtualization */}
                  <div
                    className="cal-names cal-body-names"
                    style={{ minHeight: `${displayedPromos.length * 38}px` }}
                  >
                    <PromoNamesSpacer height={calVirtual.topSpacer} />
                    {visiblePromos.map((p) => {
                      return (
                        <div
                          key={p.id}
                          className="cal-row promo-row"
                          tabIndex={0}
                          role="button"
                          aria-label={`Open ${p.name}`}
                          onClick={() => openPromo(p.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") openPromo(p.id);
                          }}
                        >
                          <span
                            className="dot"
                            style={{ background: chColor[p.ch] || "var(--primary)" }}
                          />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="pname-line">
                              <span className="pid-badge">{p.id}</span>
                              <span className="pname" title={p.name}>
                                {p.name}
                              </span>
                            </div>
                            <div className="pid">
                              <span>{p.ch}</span>
                              <span className="sep">·</span>
                              <span>{p.status}</span>
                              {p.disc ? (
                                <span className="disc-chip">{pct(p.disc)}</span>
                              ) : null}
                              {p.leakN ? (
                                <span className="leak-chip">{p.leakN} leaks</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <PromoNamesSpacer height={calVirtual.bottomSpacer} />
                  </div>

                  {/* Scrollable calendar matrix body with React Virtualization */}
                  <div
                    className="cal-body-matrix"
                    onMouseOver={handleMatrixMouseOver}
                    onMouseOut={handleMatrixMouseOut}
                    onClick={handleMatrixClick}
                    style={{ minHeight: `${displayedPromos.length * 38}px` }}
                  >
                    <PromoMatrixSpacer height={calVirtual.topSpacer} />
                    <div
                      className="grid grid-body"
                      style={{
                        gridTemplateRows: `repeat(${visiblePromos.length}, 38px)`,
                      }}
                    >
                      {nights.map((x, k) => {
                        const dateObj = dObj(x.d);
                        const isFirstOfMonth = dateObj.getDate() === 1 || k === 0;
                        const isWeekend =
                          dateObj.getDay() === 0 || dateObj.getDay() === 6;

                        return (
                          <React.Fragment key={x.d}>
                            {visiblePromos.map((p) => {
                              const pData = promoLookup.get(p.id);
                              const cellCode = pData ? pData.codeRow[k] : undefined;
                              const leakAmt = pData ? (pData.leakRow[k] || 0) : 0;

                              return (
                                <button
                                  key={p.id}
                                  className={`cell promo-cell ${isFirstOfMonth ? "mon-start" : ""} ${isWeekend ? "weekend" : ""}`}
                                  type="button"
                                  tabIndex={cellCode ? 0 : -1}
                                  aria-label={`${p.id} ${dmd(x.d)} ${cellCode ? (cellCode === "OK" ? "safe" : LEAK_NAME[cellCode]) : "not bookable"}`}
                                  data-night={k}
                                  data-pid={p.id}
                                  data-code={cellCode || ""}
                                  data-leak={leakAmt}
                                >
                                  <span
                                    className="cell-pill"
                                    style={{
                                      background: cellCode
                                        ? CELL_BG[cellCode]
                                        : "var(--c-off)",
                                    }}
                                  />
                                </button>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <PromoMatrixSpacer height={calVirtual.bottomSpacer} />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* TWO COLUMN SECTION */}
          <div className="two">
            {/* LEAKS TO FIX */}
            <section className="panel" aria-label="Leakage">
              <div className="phead">
                <div>
                  <h2>Leaks to fix</h2>
                  <p>
                    Ranked by net revenue at risk. RevEVOLVE recommends; the RM
                    makes the change in the extranet.
                  </p>
                </div>
                <button
                  className="btn"
                  type="button"
                  disabled={!selectableLeaks.length}
                  onClick={handleQueueLeaks}
                >
                  {selectableLeaks.length
                    ? `Send ${selectableLeaks.length} fixes to RM`
                    : "No fixes selected"}
                </button>
              </div>

              <div
                className="filters pad"
                style={{ margin: 0, paddingBottom: 0 }}
              >
                <select
                  aria-label="Leak rule"
                  value={leakFilter.rule}
                  onChange={(e) =>
                    setLeakFilter((prev) => ({ ...prev, rule: e.target.value }))
                  }
                >
                  <option value="">All leak rules</option>
                  <option value="L1">L1 blackout night</option>
                  <option value="L2">L2 below hurdle</option>
                  <option value="L3">L3 stacking</option>
                </select>

                <select
                  aria-label="Promotion"
                  value={leakFilter.promo}
                  onChange={(e) =>
                    setLeakFilter((prev) => ({ ...prev, promo: e.target.value }))
                  }
                >
                  <option value="">All promotions</option>
                  {pr
                    .filter((p) => p.leakN > 0)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.id} {p.name}
                      </option>
                    ))}
                </select>

                <span className="reason">
                  {filteredLeaks.length} nights,{" "}
                  {fmt$(filteredLeaks.reduce((t, l) => t + l.amt, 0))}
                </span>
              </div>

              <div className="tablewrap leaks-tablewrap" style={{ maxHeight: "calc(100vh - 350px)", minHeight: "300px", overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>
                        <span className="sr">Select</span>
                      </th>
                      <th>Night</th>
                      <th>Promotion</th>
                      <th>Rule</th>
                      <th className="r">At risk</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeaks.length > 0 ? (
                      filteredLeaks.slice(0, 40).map((l) => {
                        const p = pr[l.i];
                        const x = nights[l.k];
                        const isQueued = queuedLeaks.has(l.key);
                        const isChecked = !uncheckedLeaks.has(l.key);
                        return (
                          <tr key={l.key}>
                            <td>
                              {isQueued ? (
                                <span className="badge c-OK">Queued</span>
                              ) : (
                                <input
                                  className="ck"
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setUncheckedLeaks((prev) => {
                                      const next = new Set(prev);
                                      if (checked) next.delete(l.key);
                                      else next.add(l.key);
                                      return next;
                                    });
                                  }}
                                  aria-label={`Select fix for ${p?.id} ${dmd(x?.d)}`}
                                />
                              )}
                            </td>
                            <td
                              className="click"
                              style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                              onClick={() => openNight(l.k)}
                            >
                              {dmd(x.d)}
                              <div className="reason">
                                {x.diag}, {x.dba} days out
                              </div>
                            </td>
                            <td
                              className="click"
                              style={{ cursor: "pointer" }}
                              onClick={() => openPromo(p.id)}
                            >
                              <span className="ch">
                                <span
                                  className="dot"
                                  style={{ background: chColor[p.ch] }}
                                />
                                {p.id}
                              </span>
                              <div className="reason">{p.name}</div>
                            </td>
                            <td>
                              <span className={`badge c-${l.code}`}>{l.code}</span>
                            </td>
                            <td className="r num">{fmt$(l.amt)}</td>
                            <td className="reason" style={{ minWidth: "180px" }}>
                              {getLeakAction(l.i, l.k)}
                            </td>
                          </tr>
                        );

                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={6}
                          className="muted"
                          style={{ padding: "20px" }}
                        >
                          No leaks match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* WHERE THE LEAK SITS */}
            <section className="panel" aria-label="Leak by promotion">
              <div className="phead">
                <div>
                  <h2>Where the leak sits</h2>
                  <p>Net revenue at risk by promotion and rule.</p>
                </div>
              </div>
              <div className="pad">
                {leakBarRows.rows.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "140px 1fr 80px",
                      gap: "10px",
                      alignItems: "center",
                      margin: "6px 0",
                      fontSize: "13px",
                    }}
                  >
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {r.label}
                    </div>
                    <div className="bar" style={{ height: "12px" }}>
                      {r.segs.map((s, idx) => (
                        <span
                          key={idx}
                          style={{
                            left: `${(s.x / leakBarRows.max) * 100}%`,
                            width: `${(s.w / leakBarRows.max) * 100}%`,
                            background: s.c,
                          }}
                        />
                      ))}
                    </div>
                    <div className="r num">{r.val}</div>
                  </div>
                ))}

                <div className="legend" style={{ marginTop: "12px" }}>
                  <span>
                    <i style={{ background: "var(--c-l1)" }} />
                    Blackout nights
                  </span>
                  <span>
                    <i style={{ background: "var(--c-l2)" }} />
                    Below hurdle
                  </span>
                  <span>
                    <i style={{ background: "var(--c-l3)" }} />
                    Stacking
                  </span>
                </div>

                <p className="reason" style={{ marginTop: "12px" }}>
                  Parity watch (L4):{" "}
                  {pr
                    .filter((p) => p.parityN > 0)
                    .map(
                      (p) => `${p.id} undercuts ${directCh || "Direct"} on ${p.parityN} nights`,
                    )
                    .join("; ") || "none"}
                  . Match on {directCh || "your direct channel"} or fence the
                  offer.
                </p>
              </div>
            </section>
          </div>

          {/* PROMOTION PERFORMANCE TABLE */}
          <section className="panel" aria-label="Promotion performance">
            <div className="phead">
              <div>
                <h2>Promotion performance</h2>
                <p>
                  Incremental room nights are measured against what the channel
                  would have sold anyway: last year's channel volume adjusted for
                  how the rest of the hotel is pacing.
                </p>
              </div>
            </div>
            <div
              className="tablewrap perf-tablewrap"
              ref={perfScrollRef}
              onScroll={(e) => {
                perfVirtual.handleScroll();
                const el = e.currentTarget;
                if (!el.classList.contains("is-scrolling")) {
                  el.classList.add("is-scrolling");
                }
                clearTimeout(scrollTimerRef.current);
                scrollTimerRef.current = setTimeout(() => {
                  el.classList.remove("is-scrolling");
                }, 100);
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Promotion</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th className="r">Discount</th>
                    <th className="r">Room nights</th>
                    <th className="r">Net ADR</th>
                    <th>Incremental</th>
                    <th className="r">Net impact</th>
                    <th className="r">ROI</th>
                    <th>Verdict</th>
                    <th>Health</th>
                    <th className="r">Leak nights</th>
                  </tr>
                </thead>
                <tbody>
                  {pr.length === 0 ? (
                    <tr>
                      <td
                        colSpan={12}
                        style={{
                          textAlign: "center",
                          padding: "32px",
                          color: "var(--muted)",
                          fontSize: "13px",
                        }}
                      >
                        No promotions available.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {perfVirtual.topSpacer > 0 && (
                        <tr style={{ height: `${perfVirtual.topSpacer}px` }} aria-hidden="true">
                          <td colSpan={12} style={{ padding: 0, border: 0, height: `${perfVirtual.topSpacer}px` }} />
                        </tr>
                      )}
                      {visiblePerfPromos.map((p) => {
                      const f = p.perf || {};
                    const rc =
                      f.health == null
                        ? "var(--muted)"
                        : f.health >= 75
                          ? "var(--ok)"
                          : f.health >= 50
                            ? "var(--gold)"
                            : "var(--l1)";

                    return (
                      <tr
                        key={p.id}
                        className="click"
                        tabIndex={0}
                        onClick={() => openPromo(p.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") openPromo(p.id);
                        }}
                      >
                        <td>
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                          <div className="reason">
                            {p.id}, {p.type}
                          </div>
                        </td>
                        <td>
                          <span className="ch">
                            <span
                              className="dot"
                              style={{ background: chColor[p.ch] }}
                            />
                            {p.ch}
                          </span>
                        </td>
                        <td>{p.status}</td>
                        <td className="r num">
                          {p.type === "Visibility"
                            ? `+${pct(p.fee)} fee`
                            : p.type === "Value-add"
                              ? "Package"
                              : pct(p.disc)}
                        </td>
                        <td className="r num">{f.G || 0}</td>
                        <td className="r num">{f.G ? fmt$(f.K) : "—"}</td>
                        <td style={{ minWidth: "120px" }}>
                          {f.G ? (
                            <>
                              <div className="bar">
                                <span
                                  style={{
                                    width: `${(f.Y || 0) * 100}%`,
                                    background: "var(--ok)",
                                  }}
                                />
                              </div>
                              <div className="reason">
                                {pct(f.Y || 0)} new demand
                              </div>
                            </>
                          ) : (
                            <span className="reason">—</span>
                          )}
                        </td>
                        <td
                          className="r num"
                          style={{
                            color: (f.AC || 0) < 0 ? "var(--l1)" : "inherit",
                          }}
                        >
                          {f.G ? fmt$(f.AC) : "—"}
                        </td>
                        <td className="r num">
                          {f.G ? (f.AD || 0).toFixed(2) : "—"}
                        </td>
                        <td>
                          <span className={`badge ${vcls(f.verdict)}`}>
                            {f.verdict}
                          </span>
                        </td>
                        <td>
                          {f.health == null ? (
                            <span className="reason">—</span>
                          ) : (
                            <div
                              className="ring"
                              style={{
                                "--p": f.health,
                                "--rc": rc,
                              }}
                            >
                              <b>{Math.round(f.health)}</b>
                            </div>
                          )}
                        </td>
                        <td
                          className="r num"
                          style={{
                            color: p.leakN ? "var(--l1)" : "inherit",
                          }}
                        >
                          {p.leakN}
                        </td>
                      </tr>
                    );
                  })
                      }
                      {perfVirtual.bottomSpacer > 0 && (
                        <>
                          {Array.from({ length: Math.min(Math.floor(perfVirtual.bottomSpacer / 49), 6) }).map((_, idx) => (
                            <tr key={`skel-bot-${idx}`} className="perf-skeleton-tr" style={{ height: "49px" }}>
                              <td>
                                <div className="skeleton-line title" style={{ width: "130px", height: "13px", background: "#e2e8f0", borderRadius: "3px" }} />
                                <div className="skeleton-line sub" style={{ width: "75px", height: "10px", background: "#f1f5f9", borderRadius: "3px", marginTop: "4px" }} />
                              </td>
                              <td><div style={{ width: "65px", height: "12px", background: "#e2e8f0", borderRadius: "4px" }} /></td>
                              <td><div style={{ width: "40px", height: "12px", background: "#e2e8f0", borderRadius: "4px" }} /></td>
                              <td><div style={{ width: "35px", height: "12px", background: "#e2e8f0", borderRadius: "4px", margin: "0 0 0 auto" }} /></td>
                              <td><div style={{ width: "25px", height: "12px", background: "#e2e8f0", borderRadius: "4px", margin: "0 0 0 auto" }} /></td>
                              <td><div style={{ width: "40px", height: "12px", background: "#e2e8f0", borderRadius: "4px", margin: "0 0 0 auto" }} /></td>
                              <td><div style={{ width: "80px", height: "12px", background: "#e2e8f0", borderRadius: "4px" }} /></td>
                              <td><div style={{ width: "40px", height: "12px", background: "#e2e8f0", borderRadius: "4px", margin: "0 0 0 auto" }} /></td>
                              <td><div style={{ width: "30px", height: "12px", background: "#e2e8f0", borderRadius: "4px", margin: "0 0 0 auto" }} /></td>
                              <td><div style={{ width: "50px", height: "16px", background: "#f1f5f9", borderRadius: "6px" }} /></td>
                              <td><div style={{ width: "26px", height: "26px", borderRadius: "50%", background: "#f1f5f9" }} /></td>
                              <td><div style={{ width: "25px", height: "12px", background: "#e2e8f0", borderRadius: "4px", margin: "0 0 0 auto" }} /></td>
                            </tr>
                          ))}
                          {perfVirtual.bottomSpacer > (6 * 49) && (
                            <tr style={{ height: `${perfVirtual.bottomSpacer - (6 * 49)}px` }} aria-hidden="true">
                              <td colSpan={12} style={{ padding: 0, border: 0, height: `${perfVirtual.bottomSpacer - (6 * 49)}px` }} />
                            </tr>
                          )}
                        </>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ROI MAP (SCATTER PLOT) */}
          <section className="panel" aria-label="ROI map">
            <div className="phead">
              <div>
                <h2>Which promotions earn their discount</h2>
                <p>
                  Right = more of the bookings are new demand. Up = more return per
                  dollar given away. Bubble size = room nights.
                </p>
              </div>
            </div>
            <div className="pad">
              <RoiScatterChart
                promos={livePromos}
                config={config}
                chColor={chColor}
                onOpenPromo={openPromo}
              />
            </div>
          </section>

          {/* NEW CAMPAIGNS ("CAMPAIGNS TO DESIGN") */}
          <section className="panel" aria-label="New campaigns">
            <div className="phead">
              <div>
                <h2>Campaigns to design</h2>
                <p>
                  From soft nights Yield Radar found. Covered means a live
                  promotion already does the job and only needs its dates
                  adjusted.
                </p>
              </div>
            </div>
            <div className="camps">
              {camps.length === 0 ? (
                <div style={{ padding: "24px", color: "var(--muted)", fontStyle: "italic", fontSize: "13px" }}>
                  No campaigns to design.
                </div>
              ) : (
                camps
                  .sort(
                    (a, b) =>
                      (a.covered ? 1 : 0) - (b.covered ? 1 : 0) ||
                      b.target - a.target,
                  )
                  .map((c, j) => (
                    <article
                      key={c.key}
                      className="camp"
                      style={{ "--cc": chColor[c.owner] }}
                    >
                      <div className="reason">
                        {c.owner}, {c.type}
                      </div>
                      <h3>{c.name}</h3>
                      <dl>
                        <dt>Offer</dt>
                        <dd>
                          {c.disc ? `${pct(c.disc)} off BAR` : "Value-add, no rate cut"}
                        </dd>
                        <dt>Nights</dt>
                        <dd>
                          {c.nights.length} ({md(c.nights[0].d)} to{" "}
                          {md(c.nights[c.nights.length - 1].d)})
                        </dd>
                        <dt>Target</dt>
                        <dd>{c.target} rooms behind last year</dd>
                        <dt>Status</dt>
                        <dd>
                          {c.covered ? (
                            <>
                              Covered by{" "}
                              <button
                                className="ghost"
                                type="button"
                                style={{ padding: "2px 8px" }}
                                onClick={() => openPromo(c.covered)}
                              >
                                {c.covered}
                              </button>
                            </>
                          ) : (
                            <b>Design new</b>
                          )}
                        </dd>
                      </dl>
                      <div
                        style={{
                          marginTop: "auto",
                          display: "flex",
                          gap: "8px",
                        }}
                      >
                        <button
                          className="ghost"
                          type="button"
                          onClick={() => openBrief(c)}
                        >
                          {c.covered ? "Adjust brief" : "Create brief"}
                        </button>
                      </div>
                    </article>
                  ))
              )}
            </div>
          </section>

          
        </div>

        {/* DRAWERS & MODALS */}
        <PromoDrawer
          isOpen={drawerState.isOpen}
          type={drawerState.type}
          nightIndex={drawerState.nightIndex}
          promoId={drawerState.promoId}
          radarData={radarData}
          onClose={closeDrawer}
          onOpenNight={openNight}
          onOpenPromo={openPromo}
          onToast={showToast}
        />

        <PromoBriefModal
          show={briefModal.show}
          campaign={briefModal.campaign}
          nights={nights}
          onClose={() => setBriefModal({ show: false, campaign: null })}
          onToast={showToast}
        />

        {showConfig && (
          <PromoConfigModal
            show={showConfig}
            onClose={() => setShowConfig(false)}
            config={configPayload}
            baseInfo={radarData.summary}
            hotelInfo={currentHotelInfo}
            dataKeys={dataPayload.dataKeys}
            isLoading={isConfigLoading}
            onSave={handleSaveConfig}
            onPreviewApply={handlePreviewApply}
          />
        )}

        {/* TOOLTIP (Zero re-renders via direct DOM ref) */}
        <div
          ref={tooltipRef}
          className="PromoRadar-tip"
          style={{ pointerEvents: "none" }}
        />



        {/* Bottom Comments section as like EMA screenshot standard */}
        {api !== 0 && (
          <Comments
            widgetDetails={para}
            editorId={"PromoRadar" + tblids.id1}
          />
        )}

        {/* Hidden combined table for Send Mail and automated table exporters */}
        <div
          style={{ display: "none" }}
          className="snapshotrow"
          rowtitle="Promo Radar"
        >
          <table id={tblids.id1} border="1" style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th colSpan={9} style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold" }}>
                  {activeHotelName} ({activePropertyCode}) - Promo Radar
                </th>
              </tr>
              <tr>
                <th colSpan={9} style={{ textAlign: "center", fontSize: "12px" }}>
                  As Of Date: {activeAsOfDate} | Range: {startDate ? moment(startDate).format("MM/DD/YYYY") : ""} - {endDate ? moment(endDate).format("MM/DD/YYYY") : ""}
                </th>
              </tr>
              <tr>
                <th colSpan={9} style={{ textAlign: "left", fontSize: "13px", fontWeight: "bold", background: "#f0f4f8" }}>
                  Promotion Performance Summary
                </th>
              </tr>
              <tr style={{ background: "#e2e8f0", fontWeight: "bold" }}>
                <th>PROMOTION</th>
                <th>CHANNEL</th>
                <th>TYPE</th>
                <th>DISCOUNT</th>
                <th>STATUS</th>
                <th>ROOM NIGHTS</th>
                <th>NET ADR</th>
                <th>NET IMPACT</th>
                <th>ROI</th>
              </tr>
            </thead>
            <tbody>
              {(pr || []).map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{p.channel}</td>
                  <td>{p.type}</td>
                  <td>{pct(p.disc)}</td>
                  <td>{p.status}</td>
                  <td style={{ textAlign: "right" }}>{p.perf?.H ? Math.round(p.perf.G) : 0}</td>
                  <td style={{ textAlign: "right" }}>{fmt$(p.perf?.H ? p.perf.N : 0)}</td>
                  <td style={{ textAlign: "right" }}>{fmt$(p.perf?.H ? p.perf.AC : 0)}</td>
                  <td style={{ textAlign: "right" }}>{p.perf?.H && p.perf.ROI != null ? p.perf.ROI.toFixed(2) : "0.00"}</td>
                </tr>
              ))}
              <tr>
                <th colSpan={9} style={{ textAlign: "left", fontSize: "13px", fontWeight: "bold", background: "#f0f4f8" }}>
                  Where the Leak Sits (Revenue at Risk)
                </th>
              </tr>
              <tr style={{ background: "#e2e8f0", fontWeight: "bold" }}>
                <th>DATE</th>
                <th>DAYS OUT</th>
                <th>PROMOTION</th>
                <th>RULE</th>
                <th>HURDLE</th>
                <th>BAR</th>
                <th>PROMO NET</th>
                <th>AT RISK</th>
                <th>RECOMMENDED ACTION</th>
              </tr>
              {(leaks || []).map((l, idx) => {
                const x = (nights || [])[l.k];
                const p = (pr || [])[l.pIdx];
                return (
                  <tr key={idx}>
                    <td>{x ? x.d : ""}</td>
                    <td>{x ? x.lead : ""}</td>
                    <td>{p ? p.name : ""}</td>
                    <td>{LEAK_NAME[l.rule] || l.rule}</td>
                    <td style={{ textAlign: "right" }}>{fmt$(l.hurdle)}</td>
                    <td style={{ textAlign: "right" }}>{fmt$(l.bar)}</td>
                    <td style={{ textAlign: "right" }}>{fmt$(l.net)}</td>
                    <td style={{ textAlign: "right" }}>{fmt$(l.amt)}</td>
                    <td>{p && x ? getLeakAction(p, x, l) : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Widget Detail Modal */}
        {widgetModal && (
          <WidgetDetailModal
            showModal={widgetModal}
            closeModal={closeWidgetModal}
            data={widgetDetailRef.current}
          />
        )}
        </div>
      </div>
    </div>
  );
};

// ROI Scatter Plot Sub-component
function RoiScatterChart({ promos, config, chColor, onOpenPromo }) {
  const W = 900;
  const H = 340;
  const L = 60;
  const R = 35;
  const T = 30;
  const B = 40;

  const maxRoi = Math.max(2, ...promos.map((p) => p.perf?.AD || 0));
  const minRoi = Math.min(-2, ...promos.map((p) => p.perf?.AD || 0));

  // Determine nice step for ~5 to 6 ticks
  const span = Math.max(maxRoi, 2);
  const roughStep = Math.max(span / 5, 0.5);
  const power = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const fraction = roughStep / power;
  let niceFraction = 1;
  if (fraction >= 3.5) niceFraction = 5;
  else if (fraction >= 1.5) niceFraction = 2;
  const step = Math.max(niceFraction * power, 1);

  // Give generous headroom so highest bubble doesn't touch the top border
  const yMax = Math.max(Math.ceil((maxRoi * 1.15) / step) * step, step * 2);
  const yMin = minRoi < -step ? Math.floor(minRoi / step) * step : -2;

  const yTicks = [];
  if (step <= 2) {
    for (let v = yMin; v <= yMax + 0.001; v += step) {
      yTicks.push(v);
    }
  } else {
    for (let v = 0; v <= yMax + step * 0.001; v += step) {
      yTicks.push(v);
    }
    if (yMin < -step) {
      yTicks.unshift(yMin);
    }
  }

  const xMax = 1.05;

  const X = (v) =>
    L + (Math.min(xMax, Math.max(0, v)) / xMax) * (W - L - R);
  const Y = (v) =>
    T +
    (1 - (Math.min(yMax, Math.max(yMin, v)) - yMin) / (yMax - yMin)) *
    (H - T - B);

  const maxG = Math.max(...promos.map((p) => p.perf?.G || 0), 1);

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${W} ${H}`}
      style={{ overflow: "visible" }}
      role="img"
      aria-label="ROI versus incrementality"
    >
      {/* Chart border */}
      <rect
        x={L}
        y={T}
        width={W - L - R}
        height={H - T - B}
        fill="none"
        stroke="var(--line)"
      />

      {/* Scale zone */}
      <rect
        x={X(config?.scaleIncr || 0.4)}
        y={T}
        width={W - R - X(config?.scaleIncr || 0.4)}
        height={Math.max(0, Y(config?.scaleROI || 1) - T)}
        fill="var(--ok)"
        opacity=".08"
      />

      {/* Losing money zone */}
      <rect
        x={L}
        y={Y(0)}
        width={W - L - R}
        height={Math.max(0, H - B - Y(0))}
        fill="var(--l1)"
        opacity=".06"
      />

      {/* Zero line */}
      <line
        x1={L}
        x2={W - R}
        y1={Y(0)}
        y2={Y(0)}
        stroke="var(--muted)"
        strokeDasharray="3 3"
      />

      {/* Zone labels */}
      <text
        x={X(config?.scaleIncr || 0.4) + 8}
        y={Math.max(T + 12, Y(config?.scaleROI || 1) - 8)}
        fontSize="11"
        fontWeight="600"
        fill="var(--ok)"
      >
        Scale zone: ROI ≥ {config?.scaleROI || 1}, incrementality ≥{" "}
        {pct(config?.scaleIncr || 0.4)}
      </text>

      <text
        x={W - R - 6}
        y={H - B - 8}
        textAnchor="end"
        fontSize="11"
        fontWeight="600"
        fill="var(--l1)"
      >
        Losing money
      </text>

      {/* Y axis ticks */}
      {yTicks.map((v) => (
        <text
          key={v}
          x={L - 8}
          y={Y(v) + 3}
          textAnchor="end"
          fontSize="10"
          fill="var(--muted)"
          fontFamily="Roboto, sans-serif"
        >
          {v}
        </text>
      ))}

      {/* X axis ticks */}
      {[0, 0.25, 0.5, 0.75, 1].map((v) => (
        <text
          key={v}
          x={X(v)}
          y={H - B + 14}
          textAnchor="middle"
          fontSize="10"
          fill="var(--muted)"
          fontFamily="Roboto, sans-serif"
        >
          {pct(v)}
        </text>
      ))}

      {/* Axis titles */}
      <text
        x={(L + W - R) / 2}
        y={H - 6}
        textAnchor="middle"
        fontSize="11"
        fill="var(--muted)"
      >
        Incrementality (share of bookings that are new demand)
      </text>
      <text
        transform={`translate(14 ${(T + H - B) / 2}) rotate(-90)`}
        textAnchor="middle"
        fontSize="11"
        fill="var(--muted)"
      >
        ROI
      </text>

      {/* Promotion bubbles */}
      {promos.some((p) => (p.perf?.G || 0) > 0) ? (
        promos.map((p) => {
          if (!(p.perf?.G || 0)) return null;
          const rr = 7 + 16 * Math.sqrt((p.perf?.G || 0) / maxG);
          const cx = Math.min(W - R - rr, Math.max(L + rr, X(p.perf?.Y || 0)));
          const cy = Math.min(H - B - rr, Math.max(T + rr, Y(p.perf?.AD || 0)));
          const col = chColor[p.ch] || "var(--brand)";

          // Text placement: if bubble is too close to top (cy - rr - 4 < T), place label below bubble
          const isTopClipped = cy - rr - 4 < T;
          const textY = isTopClipped ? cy + rr + 13 : cy - rr - 4;

          return (
            <g
              key={p.id}
              style={{ cursor: "pointer" }}
              tabIndex={0}
              role="button"
              aria-label={`${p.name}, ROI ${(p.perf?.AD || 0).toFixed(2)}`}
              onClick={() => onOpenPromo(p.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onOpenPromo(p.id);
              }}
            >
              <circle
                cx={cx}
                cy={cy}
                r={rr}
                fill={col}
                fillOpacity=".25"
                stroke={col}
                strokeWidth="1.5"
              />
              <text
                x={cx}
                y={textY}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill="var(--text)"
              >
                {p.id}
              </text>
            </g>
          );
        })
      ) : (
        <text
          x={(L + W - R) / 2}
          y={(T + H - B) / 2}
          textAnchor="middle"
          fontSize="12"
          fill="var(--muted)"
        >
          No promotion bookings recorded
        </text>
      )}
    </svg>
  );
}


export default PromoRadar;
